import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import axios from 'axios';

// ======================================================================================
// Tipos
// ======================================================================================
export enum TipoConselho
{
    CRM = 0,
    CRMV = 1,
    CRO = 2,
    CRF = 3,
    CREFITO = 4,
    CRN = 5,
    CRP = 6,
    COREN = 7,
    CRBM = 8,
    CREF = 9,
    CRESS = 10,
}

export interface Medico {
  id: number;
  codigo: number;
  tipoConselho: TipoConselho;
  uf: string;
  registro: string;
  nome: string;
}

export interface SalvarMedicoDTO {
    id?: number;
    tipoConselho: TipoConselho;
    uf: string;
    registro: string;
    nome: string;
}

export interface PageResult<T> {
  data: T[];
  total: number;
  pageIndex: number;
  pageSize: number;
}


// ======================================================================================
// Query Keys
// ======================================================================================
export const medicoQueryKeys = {
  all: ['medicos'] as const,
  lists: () => [...medicoQueryKeys.all, 'list'] as const,
  list: (filters: { page: number; pageSize: number; nome?: string }) =>
    [...medicoQueryKeys.lists(), filters] as const,
  details: () => [...medicoQueryKeys.all, 'detail'] as const,
  detail: (id: number) => [...medicoQueryKeys.details(), id] as const
};


// ======================================================================================
// Hooks
// ======================================================================================
export const useMedicos = (
  page: number = 1,
  pageSize: number = 10,
  nome?: string
) => {
  return useQuery<Medico[]>({
    queryKey: medicoQueryKeys.list({ page, pageSize, nome }),
    queryFn: async () => {
      const { data } = await axios.get('/api/medico', {
        params: { page, pageSize, nome }
      });
      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useMedico = (id?: number) => {
    return useQuery<Medico>({
        queryKey: medicoQueryKeys.detail(id!),
        queryFn: async () => {
            const { data } = await axios.get(`/api/medico/${id}`);
            return data;
        },
        enabled: !!id
    });
}

export const useSalvarMedico = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (medico: SalvarMedicoDTO) => {
            if (medico.id) {
                return axios.put(`/api/medico/${medico.id}`, medico);
            }
            return axios.post('/api/medico', medico);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: medicoQueryKeys.all });
        }
    });
}

export const useRemoverMedico = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => axios.delete(`/api/medico/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: medicoQueryKeys.all });
        }
    });
} 